var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, TextField, Typography } from "@mui/material";
import { Button } from "@inspera/component-library";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { useEffect, useMemo, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import useStore from "../../../../Store/store";
import dayjs from "dayjs";
import _ from "lodash";
import { validateColumnSelection, validateDateTimeRange } from "../../../../Utils/util";
var Absolute = function (_a) {
    var fromTime = _a.fromTime, setFromTime = _a.setFromTime, fromDate = _a.fromDate, setFromDate = _a.setFromDate, toTime = _a.toTime, setToTime = _a.setToTime, toDate = _a.toDate, setToDate = _a.setToDate, handleClose = _a.handleClose, handleReset = _a.handleReset, anchorEl = _a.anchorEl, setAppliedLabel = _a.setAppliedLabel, selectedColumn = _a.selectedColumn;
    var _b = useStore(), queryOptions = _b.queryOptions, setPageAndQueryOptions = _b.setPageAndQueryOptions, restoreState = _b.restoreState, setRestoreState = _b.setRestoreState, columns = _b.columns;
    var _c = useState(""), errorMsg = _c[0], setErrorMsg = _c[1];
    var _d = useState(false), isApplyDisabled = _d[0], setIsApplyDisabled = _d[1];
    // Max Date Buffer (Current Date + 1 Day)
    var maxAllowedDate = useMemo(function () { return dayjs().add(1, "day"); }, []);
    // PaperProps to hide disabled years in the dropdown
    var datePickerPaperProps = useMemo(function () { return ({
        sx: {
            "& .PrivatePickersYear-yearButton.Mui-disabled, & .MuiPickersYear-yearButton.Mui-disabled": {
                display: "none",
            },
        },
    }); }, []);
    var ALLOWED_COLUMNS = useMemo(function () {
        return columns
            .filter(function (col) { return col.column_data_type === "date"; })
            .map(function (col) { return col.column_name; });
    }, [columns]);
    // Cleaned up useEffect using the utility function
    useEffect(function () {
        // 1. Validate Column First
        var columnValidation = validateColumnSelection(selectedColumn, ALLOWED_COLUMNS);
        if (!columnValidation.isValid) {
            setIsApplyDisabled(true);
            setErrorMsg(columnValidation.errorMsg);
            return; // Exit early
        }
        // 2. Validate Dates if Column is valid
        var dateValidation = validateDateTimeRange(fromDate, fromTime, toDate, toTime, maxAllowedDate);
        setIsApplyDisabled(!dateValidation.isValid);
        setErrorMsg(dateValidation.errorMsg);
    }, [fromDate, fromTime, toDate, toTime, selectedColumn, ALLOWED_COLUMNS]);
    var handleSave = function () {
        // Run final safety checks using the same isolated utilities
        var columnValidation = validateColumnSelection(selectedColumn, ALLOWED_COLUMNS);
        var dateValidation = validateDateTimeRange(fromDate, fromTime, toDate, toTime, maxAllowedDate);
        if (!columnValidation.isValid) {
            setErrorMsg(columnValidation.errorMsg);
            return;
        }
        if (!dateValidation.isValid) {
            setErrorMsg(dateValidation.errorMsg);
            return;
        }
        if (fromDate == null || toDate == null || fromTime == null || toTime == null) {
            setErrorMsg("Please select all date and time values.");
            return;
        }
        // Combine for the actual payload
        var fromDateCombined = fromDate
            .clone()
            .set("hour", fromTime.hour())
            .set("minute", fromTime.minute())
            .set("second", fromTime.second());
        var toDateCombined = toDate
            .clone()
            .set("hour", toTime.hour())
            .set("minute", toTime.minute())
            .set("second", toTime.second());
        var newQueryOptions = _.cloneDeep(queryOptions);
        var finalFromDate = fromDateCombined.add(fromTime.utcOffset(), "minute");
        var finalToDate = toDateCombined.add(toTime.utcOffset(), "minute");
        newQueryOptions.date_range = {
            field_name: selectedColumn,
            gte: finalFromDate.toISOString(),
            lte: finalToDate.toISOString(),
        };
        if (restoreState)
            setRestoreState(false);
        setPageAndQueryOptions(1, newQueryOptions);
        setAppliedLabel("".concat(fromDateCombined.format("DD.MM.YYYY h:mm A"), " \u2192 ").concat(toDateCombined.format("DD.MM.YYYY h:mm A")));
        handleClose();
    };
    return (_jsxs(Box, __assign({ sx: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            margin: "20px 0",
        } }, { children: [_jsxs(Box, __assign({ marginBottom: "10px" }, { children: [_jsx(Typography, __assign({ sx: {
                            margin: "5px 0",
                            color: "#272727",
                            fontSize: "16px",
                            fontWeight: "400",
                        } }, { children: "From" })), _jsxs(Box, __assign({ sx: { display: "flex" } }, { children: [_jsx(Box, __assign({ marginRight: "10px" }, { children: _jsx(LocalizationProvider, __assign({ dateAdapter: AdapterDayjs }, { children: _jsx(DatePicker, { label: "Select Date", value: fromDate, onChange: function (val) { return setFromDate(val); }, renderInput: function (params) { return _jsx(TextField, __assign({}, params)); }, PopperProps: {
                                            anchorEl: anchorEl,
                                            placement: "auto-start",
                                        }, inputFormat: "DD-MM-YYYY", maxDate: maxAllowedDate, PaperProps: datePickerPaperProps }) })) })), _jsx(LocalizationProvider, __assign({ dateAdapter: AdapterDayjs }, { children: _jsx(TimePicker, { value: fromTime, onChange: function (newTime) { return setFromTime(newTime); }, label: "Select Time(UTC)", renderInput: function (params) { return _jsx(TextField, __assign({}, params)); }, PopperProps: {
                                        anchorEl: anchorEl,
                                        placement: "auto-start",
                                    } }) }))] }))] })), _jsxs(Box, { children: [_jsx(Typography, __assign({ sx: {
                            margin: "5px 0",
                            color: "#272727",
                            fontSize: "16px",
                            fontWeight: "400",
                        } }, { children: "To" })), _jsxs(Box, __assign({ sx: { display: "flex" } }, { children: [_jsx(Box, __assign({ marginRight: "10px" }, { children: _jsx(LocalizationProvider, __assign({ dateAdapter: AdapterDayjs }, { children: _jsx(DatePicker, { value: toDate, onChange: function (val) { return setToDate(val); }, renderInput: function (params) { return _jsx(TextField, __assign({}, params)); }, label: "Select Date", PopperProps: {
                                            anchorEl: anchorEl,
                                            placement: "auto-start",
                                        }, inputFormat: "DD-MM-YYYY", maxDate: maxAllowedDate, PaperProps: datePickerPaperProps }) })) })), _jsx(LocalizationProvider, __assign({ dateAdapter: AdapterDayjs }, { children: _jsx(TimePicker, { value: toTime, onChange: function (newTime) { return setToTime(newTime); }, label: "Select Time(UTC)", renderInput: function (params) { return _jsx(TextField, __assign({}, params)); }, PopperProps: {
                                        anchorEl: anchorEl,
                                        placement: "auto-start",
                                    } }) }))] }))] }), _jsx(Box, __assign({ minHeight: "30px", paddingTop: "10px" }, { children: errorMsg.length > 0 && (_jsx(Typography, __assign({ sx: {
                        margin: "5px 0",
                        color: "#d10000",
                        fontSize: "13px",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        backgroundColor: "#edc0c0",
                        width: "fit-content",
                    } }, { children: errorMsg }))) })), _jsxs(Box, __assign({ sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    width: "175px",
                    marginTop: "20px",
                } }, { children: [_jsx(Button, __assign({ onClick: handleSave, variant: "contained", color: "primary", disabled: isApplyDisabled }, { children: "Apply" })), _jsx(Button, __assign({ onClick: function () { return handleReset(); }, variant: "contained", color: "secondary" }, { children: "Reset" }))] }))] })));
};
export default Absolute;
